import * as React from "react";
import { Calendar as CalendarIcon, ChevronDown } from "lucide-react";
import { DateRange } from "react-day-picker";
import { format, subDays, startOfYear } from "date-fns";
import { cn } from "../../components/primitives/utils";
import { Button } from "../../components/primitives/button";
import { Calendar } from "../../components/primitives/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../components/primitives/popover";
import { currentTime } from "@shared-core/domain/current-time";

export type DateRangePreset = "7d" | "30d" | "90d" | "all" | "custom";

interface DateRangePickerProps {
  date: DateRange | undefined;
  onDateChange: (date: DateRange | undefined) => void;
  preset: DateRangePreset;
  onPresetChange: (preset: DateRangePreset) => void;
  className?: string;
}

export function DateRangePicker({
  date,
  onDateChange,
  preset,
  onPresetChange,
  className,
}: DateRangePickerProps) {
  const [isOpen, setIsOpen] = React.useState(false);

  const handlePresetChange = (newPreset: DateRangePreset) => {
    onPresetChange(newPreset);
    const end = currentTime();
    let start = currentTime();

    switch (newPreset) {
      case "7d":
        start = subDays(end, 7);
        break;
      case "30d":
        start = subDays(end, 30);
        break;
      case "90d":
        start = subDays(end, 90);
        break;
      case "all":
        start = startOfYear(new Date(2000, 0, 1));
        break;
      case "custom":
        return;
    }

    onDateChange({ from: start, to: end });
    // The "custom" branch returns above, so any preset reaching here closes the popover.
    setIsOpen(false);
  };

  const getDisplayText = () => {
    switch (preset) {
      case "7d":
        return "Last 7 days";
      case "30d":
        return "Last 30 days";
      case "90d":
        return "Last 90 days";
      case "all":
        return "All time";
      case "custom":
        if (date?.from) {
          if (date.to) {
            return `${format(date.from, "d MMM")} - ${format(date.to, "d MMM yyyy")}`;
          }
          return format(date.from, "d MMM yyyy");
        }
        return "Custom range";
      default:
        return "Last 30 days";
    }
  };

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={cn(
            "justify-start text-left font-normal",
            !date && "text-muted-foreground",
            className,
          )}
        >
          <CalendarIcon className="mr-2 h-4 w-4" />
          {getDisplayText()}
          <ChevronDown className="ml-auto h-4 w-4 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <div className="p-3 space-y-3">
          {/* Preset buttons */}
          <div className="grid grid-cols-2 gap-2">
            <Button
              variant={preset === "7d" ? "default" : "outline"}
              size="sm"
              onClick={() => handlePresetChange("7d")}
              className="justify-center"
            >
              Last 7 days
            </Button>
            <Button
              variant={preset === "30d" ? "default" : "outline"}
              size="sm"
              onClick={() => handlePresetChange("30d")}
              className="justify-center"
            >
              Last 30 days
            </Button>
            <Button
              variant={preset === "90d" ? "default" : "outline"}
              size="sm"
              onClick={() => handlePresetChange("90d")}
              className="justify-center"
            >
              Last 90 days
            </Button>
            <Button
              variant={preset === "all" ? "default" : "outline"}
              size="sm"
              onClick={() => handlePresetChange("all")}
              className="justify-center"
            >
              All time
            </Button>
          </div>

          {/* Divider */}
          <div className="border-t border-border" />

          {/* Calendar for custom range */}
          <div>
            <div className="text-xs text-muted-foreground mb-2 px-3">
              Custom range
            </div>
            <Calendar
              autoFocus
              mode="range"
              defaultMonth={date?.from}
              selected={date}
              onSelect={(newDate) => {
                onDateChange(newDate);
                onPresetChange("custom");
              }}
              numberOfMonths={2}
            />
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
